<template>
  <div class="flex flex-col place-items-center overflow-auto w-full">
    <BaseCard :title="'About Me'" :highlightLastWord=true>
      <p class="flex flex-col gap-2">
        <span class="block">Hello! My name is Clair, I'm from Cleveland, Ohio, and I am a senior at the <span class="font-bold">University of Notre Dame</span>, studying Computer Science and Mathematics! My passions center in 
          <span class="font-bold">Human-Computer Interaction</span>, <span class="font-bold">Machine Learning</span>,
          Full-Stack Development, and Optimization. My work primarily focuses around
          <span class="font-bold">dark patterns</span>,
          <span class="font-bold">AI Alignment</span>, and
          <span class="font-bold">accessible prototyping design</span>. My story began with a simple calculator program in high school physics for the quadratic formula, and my love for bringing my ideas to life through code soared. Now, I express myself most wholly through my creations, from tinkering with the newest AI agent to designing entire websites and shortcuts to make life easier.
          Now, I express myself most wholly with my creations. I take just a small <span class="font-medium">thought</span>, expand it into an <span class="font-medium">idea</span>, and bring it to <span class="font-medium">life</span>.
          I see myself as a lifelong learner, and I am always pursuing new learning opportunities wherever they appear.
        </span>
        <span class="block">
          On and off campus, I pursue my passions mainly through service organizations and leadership roles, currently through serving as PrismND's President and Notre Dame Climbing Competition Lead. These provide me with substantial opportunities to engage in my community by leading projects and developing meaningful relationships with people from all areas. I use my skills to create collaborative environments and complete complex projects, including all around me while still staying prepared.  
          In the summer of 2026, I am a Software Engineering Intern at 84.51° in Cincinnati, Ohio, where I am creating an
          agentic AI system toward internal system upgrades.
        </span>
        <span class="block">
          Beyond my studies, I have many hobbies that I enjoy!  I spend just as much time inside as I do outside, particularly rock climbing, writing poetry, hiking, playing video games, and researching any topic that interests me in the moment!
        </span> 
      </p>
      <h2>Some photos of me!</h2>
      <HorizontalScroll :photoBoxes="photoBoxes"></HorizontalScroll>
    </BaseCard>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import BaseCard from '@/features/global/BaseCard.vue';
import HorizontalScroll from '@/features/global/HorizontalScroll.vue';

const imageContext = require.context('@/assets/AboutMe', true, /\.(png|jpe?g|gif|svg)$/i);

interface PhotoBoxImage {
  src: string;
  title: string;
  sortOrder: number;
}

const formatPhotoTitle = (relativePath: string) => {
  const withoutExtension = relativePath.replace(/\.[^.]+$/, '');
  const fileName = withoutExtension.split('/').pop() ?? withoutExtension;

  return fileName
    .replace(/^\d+\s*[-_]*\s*/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const getPhotoSortOrder = (relativePath: string) => {
  const withoutExtension = relativePath.replace(/\.[^.]+$/, '');
  const fileName = withoutExtension.split('/').pop() ?? withoutExtension;
  const match = fileName.match(/^(\d+)/);

  return match ? Number(match[1]) : Number.POSITIVE_INFINITY;
};

const getImageGroups = (): Record<string, PhotoBoxImage[]> => {
  const groupedImages = imageContext.keys().reduce<Record<string, PhotoBoxImage[]>>((accumulator, key) => {
    const relativePath = key.replace('./', '');
    const folderName = relativePath.split('/').length > 1 ? relativePath.split('/')[0] : 'Root';
    const resolvedImage = imageContext(key);

    if (!resolvedImage) {
      return accumulator;
    }

    if (!accumulator[folderName]) {
      accumulator[folderName] = [];
    }

    accumulator[folderName].push({
      src: resolvedImage,
      title: formatPhotoTitle(relativePath),
      sortOrder: getPhotoSortOrder(relativePath),
    });
    return accumulator;
  }, {});

  return Object.fromEntries(
    Object.entries(groupedImages)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([folderName, images]) => [folderName, [...images].sort((left, right) => left.sortOrder - right.sortOrder || left.title.localeCompare(right.title))]),
  );
};

const photoBoxes = computed<Record<string, PhotoBoxImage[]>>(() => {
  const groupedImages = getImageGroups();

  return Object.entries(groupedImages).reduce<Record<string, PhotoBoxImage[]>>((accumulator, [folderName, images]) => {
    accumulator[folderName] = images;
    return accumulator;
  }, {});
});
</script>
